import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  makeStyles,
  Theme,
  createStyles,
  Box,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Stamp, StampModel } from '../../../models/DailyReport/StampModel';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    emojiGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
      gap: '10px',
      justifyItems: 'center',
      padding: theme.spacing(2),
      maxWidth: '100%',
      overflowX: 'hidden',
    },
    emojiButton: {
      fontSize: '24px',
      padding: theme.spacing(1),
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      textAlign: 'center',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderRadius: '4px',
      },
    },
  })
);

// 基本的な感情を表す絵文字を中心に選定
const emojis = [
  '🙂',
  '😄',
  '😊',
  '👍',
  '👋',
  '✨',
  '💡',
  '⭐', // 基本的な絵文字
  '😃',
  '😉',
  '🎵',
  '💪',
  '✅',
  '📌',
  '❤️',
  '👏', // シンプルな絵文字
];

// フォールバック用のテキスト定義
const emojiFallbacks: { [key: string]: string } = {
  '🙂': ':)',
  '😄': ':D',
  '😊': ':)',
  '👍': '(OK)',
  '👋': '(bye)',
  '✨': '*',
  '💡': '(idea)',
  '⭐': '*',
  '😃': ':D',
  '😉': ';)',
  '🎵': '♪',
  '💪': '(go)',
  '✅': 'v',
  '📌': '(pin)',
  '❤️': '<3',
  '👏': '(clap)',
};

interface EmojiPickerProps {
  open: boolean;
  onClose: () => void;
  onStampSelect: (stamp: Stamp) => void;
  // onEmojiSelect: (emoji: string) => void;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ open, onClose, onStampSelect }) => {
  const classes = useStyles();

  const [stamps, setStamps] = React.useState<Stamp[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleStampClick = (stamp: Stamp) => {
    // 絵文字のサポートチェック
    // const isEmojiSupported = (emoji: string) => {
    //   const canvas = document.createElement('canvas');
    //   const ctx = canvas.getContext('2d');
    //   if (!ctx) return false;

    //   ctx.fillStyle = '#000';
    //   ctx.textBaseline = 'top';
    //   ctx.font = '32px Arial';
    //   ctx.fillText(emoji, 0, 0);

    //   return ctx.getImageData(16, 16, 1, 1).data[3] !== 0;
    // };

    // 絵文字がサポートされていない場合はフォールバックテキストを使用
    // const finalEmoji = isEmojiSupported(emoji) ? emoji : emojiFallbacks[emoji];
    onStampSelect(stamp);
    onClose();
  };

  const fetchStamps = async () => {
    try {
      setIsLoading(true);
      const response = await StampModel.all();
      setStamps(response);
    } catch (error) {
      console.error('Failed to fetch stamps:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStamps();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        スタンプを選択
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <DialogContent>
          <div className={classes.emojiGrid}>
            {stamps.length === 0 ? (
              <Typography
                variant="body2"
                style={{
                  textAlign: 'center',
                  color: 'gray',
                  marginTop: '20px',
                }}
              >
                😔 スタンプはありません
              </Typography>
            ) : (
              stamps.map((stamp, index) => (
                <Box
                  key={index}
                  style={{
                    position: 'relative',
                    width: '80px',
                    height: '80px',
                  }}
                >
                  <button
                    className={classes.emojiButton}
                    onClick={() => handleStampClick(stamp)}
                    title={emojiFallbacks[stamp.name ?? '']}
                    style={{
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    {stamp.path ? (
                      <img
                        src={stamp.path}
                        alt={stamp.name}
                        width="80"
                        height="80"
                        style={{ borderRadius: '5px' }}
                      />
                    ) : (
                      stamp.emoji
                    )}
                  </button>
                  <Typography
                    variant="body2"
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      textAlign: 'center',
                      borderRadius: '0 0 5px 5px',
                      padding: '2px 0',
                    }}
                  >
                    {stamp.name}
                  </Typography>
                </Box>
              ))
            )}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default EmojiPicker;
